import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { ResponsiveValues } from '@sketch/global-styles'
import { useSearch } from '@sketch/utils'
import { routes, useFlag } from '@sketch/modules-common'
import { Breakpoint, Pill, pluralize } from '@sketch/components'
import {
  NestedProjectFragment,
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'
import { UrlQueryKeys, urlQueryKeys } from '@sketch/constants'

import {
  NestedProjectItem,
  NestedProjectItemGridViewSkeleton,
  NestedProjectItemListViewSkeleton,
} from '../NestedProjectItem'
import {
  ActionTableCell,
  FocusLink,
  HeaderTableCell,
} from '../../../shares/components/DocumentItemsLayout/DocumentItemsLayout.styles'
import { Header } from '../../../projects/components/ProjectShares/ProjectShares.styles'
import {
  StorageItemFilter,
  useStorageItemsFilters,
  storageItemFiltersToFlags,
} from '../../../storageItems/hooks'
import {
  StorageItemsLayout,
  RenderStorageItem,
} from '../../../storageItems/components'
import { Action } from '../../../storageItems/components/StorageItemsLayout/StorageItemsLayout.types'

type NestedProject = NestedProjectFragment

interface CustomColumns {
  customColumns?: ResponsiveValues<number>
}

interface NestedProjectUrlProps {
  workspaceId: string
  nestedProjectId: string
  search: string | null | undefined
  filters: StorageItemFilter
}

const nestedProjectUrl = ({
  workspaceId,
  nestedProjectId,
  search,
  filters,
}: NestedProjectUrlProps): string => {
  const query: Partial<
    Record<UrlQueryKeys['filter'] | UrlQueryKeys['search'], string>
  > = {}
  if (search) {
    query[urlQueryKeys.search] = search
  }
  const flags = storageItemFiltersToFlags(filters)
  if (flags.length > 0) {
    query[urlQueryKeys.filter] = flags.join(',')
  }

  return routes.WORKSPACE_PROJECT.create({
    workspaceId,
    projectId: nestedProjectId,
    query,
  })
}

interface NestedProjectItemsLayoutProps extends CustomColumns {
  workspaceId: string

  nestedProjects: NestedProject[]
  totalCount?: number

  onLoadMore: () => Promise<any>
  renderDropdown: RenderStorageItem<StorageItemFragment>
  action?: Action
  search?: string

  canUseDesignSystemManager?: boolean
  disabledDrag?: boolean

  extraPlaceholders?: number
}

const NestedProjectItemsLayout = (props: NestedProjectItemsLayoutProps) => {
  const {
    onLoadMore,
    action: actionCreator = () => 'link',
    renderDropdown,
    nestedProjects,
    workspaceId,
    totalCount = 0,
    customColumns,
    disabledDrag = false,
    extraPlaceholders = 0,
  }: typeof props = props

  const history = useHistory()
  const isNestedProjectsOn = useFlag('nested-projects')

  const { searchDebounced: search } = useSearch()
  const { filters } = useStorageItemsFilters()

  // Make sure the onLoadMore Reference is always the latest
  const onLoadMoreRef = useRef<() => Promise<unknown>>(onLoadMore)

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  const renderGridItem = (nestedProject: StorageItemFragment) => {
    const action = actionCreator(nestedProject)

    if (action !== 'link') {
      return (
        <NestedProjectItem
          key={nestedProject.identifier}
          nestedProject={nestedProject}
          workspaceIdentifier={workspaceId}
          renderDropdown={renderDropdown}
          presentation="grid"
          onClick={action}
          disabledDrag={disabledDrag}
        />
      )
    }

    return (
      <FocusLink
        key={nestedProject.identifier}
        to={nestedProjectUrl({
          workspaceId: workspaceId,
          nestedProjectId: nestedProject.identifier,
          search: search,
          filters: filters,
        })}
      >
        <NestedProjectItem
          key={nestedProject.identifier}
          nestedProject={nestedProject}
          workspaceIdentifier={workspaceId}
          renderDropdown={renderDropdown}
          presentation="grid"
          disabledDrag={disabledDrag}
        />
      </FocusLink>
    )
  }

  const renderTableItem = (item: StorageItemFragment) => {
    const action = actionCreator(item)

    return (
      <NestedProjectItem
        nestedProject={item}
        workspaceIdentifier={workspaceId}
        renderDropdown={renderDropdown}
        presentation="list"
        disabledDrag={disabledDrag}
        onClick={event => {
          if (action !== 'link') {
            action()
            return
          }

          const link = nestedProjectUrl({
            workspaceId: workspaceId,
            nestedProjectId: item.identifier,
            search,
            filters,
          })

          if (event.metaKey) {
            /* Simulate the link opening with meta key */
            window.open(link, '_blank')
          } else {
            history.push(link)
          }
        }}
      />
    )
  }

  const renderLayout = (
    isMobile: boolean,
    customColumns?: ResponsiveValues<number>
  ) => {
    if (documentsLayout === 'LIST') {
      const headerLabel = pluralize('Folder', 'Folders', totalCount)

      return (
        <StorageItemsLayout
          tableColumns={[
            {
              label: `${totalCount} ${headerLabel}`,
              headerCell: HeaderTableCell,
            },
            {
              label: isNestedProjectsOn ? 'Items' : 'Documents',
              headerCell: HeaderTableCell,
            },
            { label: '', headerCell: ActionTableCell },
          ]}
          items={nestedProjects}
          onLoadMore={onLoadMore}
          customColumns={customColumns}
          totalCount={totalCount}
          Skeleton={NestedProjectItemListViewSkeleton}
          renderItem={renderTableItem}
          extraPlaceholders={extraPlaceholders}
        />
      )
    } else {
      return (
        <>
          {!isMobile && (
            <Header>
              Folders <Pill variant="secondary">{totalCount}</Pill>
            </Header>
          )}
          <StorageItemsLayout
            items={nestedProjects}
            onLoadMore={onLoadMore}
            customColumns={customColumns}
            totalCount={totalCount}
            Skeleton={NestedProjectItemGridViewSkeleton}
            renderItem={renderGridItem}
            extraPlaceholders={extraPlaceholders}
          />
        </>
      )
    }
  }

  return (
    <>
      <Breakpoint on="sm">
        {matches => renderLayout(!matches, customColumns)}
      </Breakpoint>
    </>
  )
}

export default NestedProjectItemsLayout
